import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddReport from './resolvers/resAddReport'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import { apiUrls, attFormats, reportHeights } from 'consts'
import AddRemoveTableSortable from 'components/AddRemoveTableSortable'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'
import AddFilter from 'pages/AddFilter'
import Entity from 'components/AddRemoveTableSortable/types/Entity'
import SelectWrapper from 'shared/components/SelectWrapper'

const submitRef = React.createRef<HTMLButtonElement>()

const AddReport: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [relationalFilters, setRelationalFilters] = useState<Array<Entity>>([])
    const [vslzReports, setVslzReports] = useState<Array<any>>([])
    const [aisReports, setAisReports] = useState<Array<any>>([])

    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

    const { id } = useParams()

    const navigate = useNavigate()

    const [reportId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const {
      register,
      setValue,
      getValues,
      handleSubmit,
      reset,
      setFocus,
      setError,
      control,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddReport,
    })

    useInit({
      setValue,
      getValues,
      id: reportId,
      reset,
      setDataLoaded,
      setShowOverlay,
      currentCustomer,
      setVslzReports,
      setAisReports
    })

    useFocusError({
      errors,
      setFocus,
    })

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/reports' text='All Reports' />}
          actionInsert={
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Report
            </Button>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const commonParams = {
              submitForm,
              data,
              setShowOverlay,
              setError,
              id: reportId,
              name: 'Report',
              successNav: '/reports',
              extraData: {
                filters: relationalFilters,
              },
            }

            return props.modalConfig !== undefined
              ? prepareSubmitFormFromModal({
                  ...commonParams,
                  modalConfig: props.modalConfig,
                })
              : prepareSubmitForm({
                  ...commonParams,
                  navigate: navigate,
                })
          })}
          id='main-form'
        >
          <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
            <Form.Control type='text' {...register('name')} />
          </InputWrapper>

          <InputWrapper label='Visualize Report' name={'vslz_report'} errors={errors}>
          <SelectWrapper
              name='vslz_report'
              isClearable={true}
              control={control}
              options={vslzReports}
              optionLabel='name'
              optionValue='id'
              onChange={(val) => {
                setValue('vslz_id', val?.id ?? '')
                setValue('vslz_query_name', val?.name ?? '')
              }}
            />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Visualize ID' name='vslz_id' errors={errors}>
            <Form.Control type='text' disabled={true} {...register('vslz_id')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Visualize Query Name' name='vslz_query_name' errors={errors}>
            <Form.Control type='text' disabled={true} {...register('vslz_query_name')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Description' name='description' errors={errors}>
            <Form.Control type='text' {...register('description')} />
          </InputWrapper>

          <InputWrapper label='Alairo Intelligence Summary' name={'ais_report'} errors={errors}>
          <SelectWrapper
              name='ais_report'
              isClearable={true}
              control={control}
              options={aisReports}
              optionLabel='name'
              optionValue='id'
            />
          </InputWrapper>

          <InputWrapper label='Format' name={'formats'} errors={errors}>
            <SelectWrapper
              name={'formats'}
              control={control}
              isClearable={true}
              options={attFormats}
              optionLabel='name'
              optionValue='id'
              isMulti={true}
              placeholder='Select Report Formats'
            />
          </InputWrapper>

          <InputWrapper label='Height' name={'height'} errors={errors}>
            <SelectWrapper
              name={'height'}
              control={control}
              isClearable={false}
              options={reportHeights}
              optionLabel='name'
              optionValue='id'
              isMulti={false}
              placeholder='Select Report Height'
            />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <Button
            type='submit'
            name='save'
            ref={(ref as any) ?? submitRef}
            className='d-none'
          ></Button>
        </Form>

        {props.modalConfig === undefined && (
          <AddRemoveTableSortable
            getAllRowsUrl={apiUrls.filtersToReports + '?id=' + id}
            addRemoveRowUrl={apiUrls.filtersToReports}
            relationalFieldName='filter_id'
            toggleActiveUrl={apiUrls.filters}
            maxTableHeight='600px'
            entityNameSingular='Filter'
            entityNamePlural='Filters'
            sortRowsUrl={apiUrls.filtersToReports + '/' + apiUrls.actionSort}
            setShowOverlay={setShowOverlay}
            editFormComponent={AddFilter}
            onAddItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                    setRelationalFilters((prevState) => [...prevState, entity])
                    return true
                  }
            }
            onRemoveItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                    setRelationalFilters((prevState) => prevState.filter((r) => r.id !== entity.id))
                    return true
                  }
            }
          />
        )}
      </>,
    )
  },
)

export default AddReport
